declare global {
    interface HTMLElement {
        addEventListeners(events: (keyof HTMLElementEventMap)[], handler: EventListenerOrEventListenerObject): void;
        getNearestForm(): HTMLFormElement | null;
        getSubmitSibling(): HTMLButtonElement | null;
        getElementsByType<K extends keyof HTMLElementTagNameMap>(parent?: ParentNode): NodeListOf<HTMLElementTagNameMap[K]>;
        scrollToBottom(): void;

    }
    interface Document {
        addEventListeners(events: (keyof DocumentEventMap)[], handler: EventListenerOrEventListenerObject): void;
        getElementsByType<K extends keyof HTMLElementTagNameMap>(parent?: ParentNode): NodeListOf<HTMLElementTagNameMap[K]>;
    }
    interface Window {
        AppConfig?: Record<string, string | number | boolean>;
    }
    interface Number {
        padZeros(totalDigits?: number): string;
    }
}

export { };
export namespace AtActive {
    export namespace Web {

        export namespace Classes {
            export namespace DataUtils {
                export class Strings {
                    static addDashesBetweenWords(str: string): string {
                        return str.replace(/(?<!^)([A-Z])(?=[a-z]+)/g, '-$1').toLowerCase();
                    }
                }
                export namespace Numbers {
                    Number.prototype.padZeros = function (totalDigits: number = 2): string {
                        const value = Number(this);
                        const isNegative = value < 0;
                        const absoluteString = Math.abs(value).toString();
                        const padded = absoluteString.padStart(totalDigits, "0");
                        return isNegative ? `-${padded}` : padded;
                    };
                }
            }

            export class ElementInterval {
                constructor(public elementId: string, private _intervalId: ReturnType<typeof setInterval>) { }
                get intervalId(): ReturnType<typeof setInterval> {
                    return this._intervalId;
                }
                clear(): void {
                    clearInterval(this._intervalId as any);
                }
            }
            export class CssUtils {
                static getCssValue(variableName: string): string | null {
                    const cssVarName = `--${variableName.replace(/^-+/, '')}`; // Add '--' and remove leading dashes
                    const rootStyle = getComputedStyle(document.documentElement);
                    const value = rootStyle.getPropertyValue(cssVarName);
                    return value ? value.trim() : null; // Trim whitespace and handle missing values
                }
            }

            export namespace CssTypes {
               export type Cursors =
                    | "auto"
                    | "default"
                    | "none"
                    | "context-menu"
                    | "help"
                    | "pointer"
                    | "progress"
                    | "wait"
                    | "cell"
                    | "crosshair"
                    | "text"
                    | "vertical-text"
                    | "alias"
                    | "copy"
                    | "move"
                    | "no-drop"
                    | "not-allowed"
                    | "grab"
                    | "grabbing"
                    | "e-resize"
                    | "n-resize"
                    | "ne-resize"
                    | "nw-resize"
                    | "s-resize"
                    | "se-resize"
                    | "sw-resize"
                    | "w-resize"
                    | "ew-resize"
                    | "ns-resize"
                    | "nesw-resize"
                    | "nwse-resize"
                    | "zoom-in"
                    | "zoom-out";
            }
            export class URI {
                private uri: string;

                constructor(uri: string) {
                    this.uri = uri;
                }

                normalize(): string {
                    const protocolMatch = this.uri.match(/^(https?:\/\/|wss?:\/\/)/);
                    const protocol = protocolMatch ? protocolMatch[0] : "";
                    const urlWithoutProtocol = protocol ? this.uri.slice(protocol.length) : this.uri;
                    const normalizedPath = urlWithoutProtocol.replace(/\/{2,}/g, "/");
                    return protocol + normalizedPath;
                }

                toString(): string {
                    return this.uri;
                }
            }

        }

        export namespace Elements {
            /*Extentions -  Interface*//*Events - HTMLElement*/
            export function HTMLElementAddListeners(this: HTMLElement, events: (keyof HTMLElementEventMap)[], handler: EventListenerOrEventListenerObject): void {
                events.forEach(event => this.addEventListener(event, handler));
            }

            export function DocumentAddListeners(this: Document, events: (keyof DocumentEventMap)[], handler: EventListenerOrEventListenerObject): void {
                events.forEach(event => this.addEventListener(event, handler));
            }

            // Extending HTMLElement and Document prototypes
            HTMLElement.prototype.addEventListeners = HTMLElementAddListeners;
            Document.prototype.addEventListeners = DocumentAddListeners;
            HTMLElement.prototype.getElementsByType = function <K extends keyof HTMLElementTagNameMap>(parent: ParentNode = document): NodeListOf<HTMLElementTagNameMap[K]> {
                const tagName = getTagNameFromElementType(this.constructor as any) as K;
                return parent.querySelectorAll(tagName);
            };

            Document.prototype.getElementsByType = function <K extends keyof HTMLElementTagNameMap>(parent: ParentNode = document): NodeListOf<HTMLElementTagNameMap[K]> {
                const tagName = getTagNameFromElementType(this.constructor as any) as K;
                return parent.querySelectorAll(tagName);
            };
            HTMLElement.prototype.getSubmitSibling = function (): HTMLButtonElement | null {
                const parentElement = this.parentNode as HTMLElement;
                for (let sibling of Array.from(parentElement.children)) {
                    if (sibling instanceof HTMLButtonElement && sibling.type === 'submit') {
                        return sibling as HTMLButtonElement; // Found the submit button!
                    }
                }
                return null; // No submit button found among siblings
            }
            HTMLElement.prototype.getNearestForm = function (): HTMLFormElement | null {
                let element: HTMLElement | null = this;
                while (element && !(element instanceof HTMLFormElement)) { element = element.parentElement; }
                return element as HTMLFormElement | null;
            };

            HTMLElement.prototype.scrollToBottom = function () {
                const lastElement = this.lastElementChild;
                if (lastElement) {
                    lastElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
                }
            };
            /*Events - HTMLElement*/
            /*Events - Document*/
            document.addEventListeners(['DOMContentLoaded'], DocLoad);
            function DocLoad(this: Document) {
                const tagDarkElement = this.querySelector('.tag-dark') as HTMLElement | null;
                if (tagDarkElement) {
                    this.documentElement.classList.add('tag-dark');
                }
                setTimeout(updateFormFieldWithAttrib, 200);
            }
            /*Events - Document*/
            /*Effects - Moving Div*/
            enum moveElementDirection {
                None,
                Vertical,
                Horizontal,
                Net
            }
            export const DefaultMoveElementDirection = moveElementDirection.Net;
            /* Variables* - Classes*/
            export const TagCenterClass: string | null = Classes.CssUtils.getCssValue('TagCenter');
            /* Variables* - Classes*/
            /* Variables* - Intervals*/
            export const MovingDivIntervals: Classes.ElementInterval[] = [];
            export const DefaultMoveTime: number = 5000;
            /* Variables* - Intervals*/
            /* Variables* - Attributes*/
            export const sharedInputAttrib: string = 'shared-id';
            /* Variables* - Attributes
            /* Variables* - Urls*/
            export const DefaultGetPropUrl: string = "/GetProperty?Name=";
            /* Variables* - Urls*/
            export class HTMLMoveDivElement extends HTMLElement {
                constructor() {
                    super(); // Always call super() first in the constructor

                }
                moveElement(Direction: moveElementDirection, TimerInterval: number): void {
                    let prevLeft: number;
                    let prevTop: number;
                    var element: HTMLElement = this;
                    if (TagCenterClass) {
                        element.classList.add(TagCenterClass);
                    }
                    function action(): void {
                        if (TagCenterClass && element.classList.contains(TagCenterClass)) {
                            const rect: DOMRect = element.getBoundingClientRect();
                            const currentTop: number = rect.top;
                            const currentLeft: number = rect.left;
                            element.style.left = `${currentLeft}px`;
                            element.style.top = `${currentTop}px`;
                            element.classList.remove(TagCenterClass);
                            prevLeft = Math.random() * (window.innerWidth - element.clientWidth);
                            prevTop = Math.random() * (window.innerHeight - element.clientHeight);
                        } else {
                            element.style.left = '0';
                            element.style.top = '0';
                            let updateLeft: boolean = [moveElementDirection.Horizontal, moveElementDirection.Net].includes(Direction);
                            let updateTop: boolean = [moveElementDirection.Vertical, moveElementDirection.Net].includes(Direction);
                            const newLeft: number = updateLeft ? Math.random() * (window.innerWidth - element.clientWidth) : prevLeft;
                            const newTop: number = updateTop ? Math.random() * (window.innerHeight - element.clientHeight) : prevTop;
                            prevLeft = newLeft;
                            prevTop = newTop;
                            element.style.transform = `translate(${newLeft}px, ${newTop}px)`;
                        }
                    }
                    if (![moveElementDirection.None].includes(Direction)) { MovingDivIntervals.push(new Classes.ElementInterval(element.id, setInterval(action, TimerInterval))); };
                };
            }

            /*Effects - Moving Div*/
            /*Elements - Get*/
            function IdentifyElementByType<K extends keyof HTMLElementTagNameMap>(elementType: new () => HTMLElementTagNameMap[K]): HTMLElementTagNameMap[K] {
                return document.createElement(getTagNameFromElementType(elementType));
            }
            function getTagNameFromElementType<K extends keyof HTMLElementTagNameMap>(elementType: new () => HTMLElementTagNameMap[K]): K {
                // Special Cases: (switch on the constructor function itself)
                switch (elementType) {
                    case HTMLAnchorElement: return "a" as K;
                    case HTMLMoveDivElement as unknown as typeof elementType: return "move-div" as K;
                    default: return Classes.DataUtils.Strings.addDashesBetweenWords(elementType.name.replace(/^(HTML)?(\w+)Element$/, "$2")).toLowerCase() as K;
                }
            }

            function getInputsWithParentIdAttribute(): HTMLInputElement[] {
                const inputs: HTMLInputElement[] = Array.from(IdentifyElementByType(HTMLInputElement).getElementsByType()) as HTMLInputElement[];
                return inputs.filter(input => input.hasAttribute(sharedInputAttrib));
            }
            /*Elements - Get*/
            /*Function - Update Form*/
            function updateFormFieldWithAttrib(): void {
                const elements: HTMLInputElement[] = getInputsWithParentIdAttribute();
                for (let i = 0; i < elements.length; i++) {
                    const element: HTMLInputElement = elements[i];
                    if (element.hasAttribute(sharedInputAttrib)) {
                        const parentIdId: string | null = element.getAttribute(sharedInputAttrib);
                        const parent: HTMLInputElement | null = document.getElementById(parentIdId!) as HTMLInputElement;
                        element.value = parent.value;
                        setFormMethod(element);
                        if (parent) {
                            parent.addEventListeners(['input', 'change'], () => {
                                element.value = parent.value;
                                setFormMethod(element)
                            });
                        }
                        function setFormMethod(element: HTMLInputElement): void {
                            const nearestForm: HTMLFormElement | null = element.getNearestForm();
                            if (nearestForm) {
                                nearestForm.method = element.value === '' ? "get" : "post";
                            }
                        }
                    }
                }
            }
            /*Function - Update Form*/
            /*Function - Ajax*/
            async function getPropertyValue(name: string, app: string): Promise<any> {
                try {
                    const response = await fetch(`${window.location.protocol}//${updateApp(app, window.location.hostname)}${DefaultGetPropUrl}${name}`);
                    if (!response.ok) {
                        const errorData = await response.json();
                        throw new Error(errorData.message || "An error occurred.");
                    }
                    const data = await response.json();
                    return data;
                } catch (error) {
                    Constants.Global.Debug(Constants.LogLevel.error, "Error fetching property value:", error);
                    throw error;
                }
            }
            /*Function - Ajax*/
            /*Function - Url*/
            function updateApp(appName: string, host: string): string {
                if (!appName) {
                    throw new Error("appName is required");
                }
                const hostParts: string[] = host.split('.');
                if (hostParts.length > 2) {
                    hostParts.splice(0, hostParts.length - 2, appName);
                } else {
                    hostParts.unshift(appName);
                }
                return hostParts.join('.');
            }
            /*Function - Url*/
            /*Function - Elements*/
            if (!customElements.get('move-div')) {
                if (!customElements.get('move-div')) customElements.define('move-div', HTMLMoveDivElement);
            }

            IdentifyElementByType(HTMLMoveDivElement).getElementsByType().forEach(element => {
                if (element instanceof HTMLMoveDivElement)
                    element.moveElement(DefaultMoveElementDirection, DefaultMoveTime); // Call your custom function
            });
        }

        /*Function - Elements*/


        /*Function - AtActive Html*/

        export namespace Html {

            export namespace DocActions {
                export class Scroll {
                    static getScrollPosition(): ScrollPosition {
                        return {
                            top: window.scrollY || document.documentElement.scrollTop,
                            left: window.scrollX || document.documentElement.scrollLeft
                        };
                    }
                    static scrollToPosition(pos: ScrollPosition): void {
                        window.scrollTo({
                            top: pos.top,
                            left: pos.left,
                            behavior: pos.behavior ?? 'smooth'
                        });
                    }
                }
                export interface ScrollPosition {
                    top: number;
                    left: number;
                    behavior?: ScrollBehavior;
                }
            }

            export interface DropdownOption {
                value: string;
                text: string;
            }

            export interface DropdownVariables {
                itemCursor: Classes.CssTypes.Cursors;
                itemClass: string;
                selectClass: string;
                scrollParams: {
                    behavior: ScrollBehavior;
                    block: ScrollLogicalPosition;
                };

            }
            interface PairJsonElement {
                Element: HTMLElement;
                ItemClass: string;
            }
            interface ResultFuncType {
                (key: any, value: any): HTMLElement;
            }
            interface ResultLoader {
                imageUrl: { valid: string; error: string; };
                tagClass: string;
            }
            export enum JsonResultTypes {
                Scalar,
                KeyValuePair,
            }
            export type HttpRequestType = 'GET' | 'POST';
            const defHtmlDopdownVariables: DropdownVariables = { itemCursor: "pointer", itemClass: "dropdown-item", selectClass: "selected", scrollParams: { behavior: 'auto', block: 'center' } };
            const defHtmlResultFunc: ResultFuncType = (key: any, value: any): HTMLElement => {
                const resultItem = document.createElement('p');
                resultItem.textContent = key.length > 0 ? `${key}:` : `${key}`;
                const valueSpan = document.createElement('strong');
                valueSpan.textContent = `${value}`;
                resultItem.appendChild(valueSpan);

                return resultItem;
            };

            /** Form Class - Handles form submission and AJAX requests */
            export class Form {
                static async GetAjaxJson<T>(
                    url: string,
                    onSuccess: ((result: any) => void) | null = null,
                    onError: ((error: any) => void) | null = null
                ): Promise<T> {
                    try {
                        const response = await fetch(url);
                        if (response.ok) {
                            const result: T = await response.json();
                            if (onSuccess) {
                                onSuccess(result);
                            }
                            return result;
                        } else {
                            Constants.Global.Debug(Constants.LogLevel.error, 'Error fetching data:', response.status, response.statusText);
                            if (onError) {
                                onError(response);
                            }
                            throw new Error(`HTTP error! status: ${response.status}`);
                        }
                    } catch (error) {
                        Constants.Global.Debug(Constants.LogLevel.error, 'Error fetching data:', error);
                        if (onError) {
                            onError(error);
                        }
                        throw error;
                    }
                }
                /**
                 * Submits a form via AJAX and handles the response as JSON.
                 * @param form
                 * @param url
                 * @param RequestType
                 * @param validationElement
                 * @param resultElement
                 * @param onSuccess
                 * @param onError
                 * @param variables
                 * @param resultFunc
                 * @param resultType
                 * @param loader
                 * @returns
                 */
                static async SubmitAjaxJson(
                    form: HTMLFormElement, url: string, RequestType: HttpRequestType = 'GET', validationElement: PairJsonElement, resultElement: PairJsonElement, onSuccess: ((result: any) => void) | null, onError: ((error: any) => void) | null, variables: DropdownVariables = defHtmlDopdownVariables, resultFunc: ResultFuncType = defHtmlResultFunc, resultType: JsonResultTypes = JsonResultTypes.KeyValuePair, loader: ResultLoader): Promise<void> {
                    form.dataset.lastSubmitTime = Date.now().toString();
                    validationElement.Element.innerHTML = ''; // Clear previous validation errors
                    validationElement.Element.style.display = 'none'; // Hide validation errors
                    resultElement.Element.innerHTML = ''; // Clear previous results
                    if (loader) {
                        const loadingOverlay = document.createElement('div');
                        loadingOverlay.className = loader.tagClass;
                        const loadingImage = document.createElement('img');
                        loadingImage.src = form.checkValidity() ? loader.imageUrl.valid : loader.imageUrl.error;
                        loadingOverlay.appendChild(loadingImage);
                        resultElement.Element.appendChild(loadingOverlay);

                    }
                    resultElement.Element.scrollIntoView(variables.scrollParams);
                    if (!form.checkValidity()) {
                        var pos: DocActions.ScrollPosition = DocActions.Scroll.getScrollPosition();
                        pos.behavior = variables.scrollParams.behavior;
                        setTimeout(() => {
                            setTimeout(() => {
                                resultElement.Element.innerHTML = '';
                            }, 5000)
                            validationElement.Element.innerHTML = ''; // Clear previous validation errors
                            validationElement.Element.style.display = 'none'; // Hide validation errors
                            DocActions.Scroll.scrollToPosition(pos);
                        }, 5000);
                        return
                    };
                    try {
                        const formData = new FormData(form);
                        const data: { [key: string]: any } = {};

                        // Convert FormData entries to an object
                        formData.forEach((value, key) => {
                            data[key] = value;
                        });

                        // Automatically identify and convert data types
                        for (const [key, value] of Object.entries(data)) {
                            const inputElement = document.getElementById(key) as HTMLInputElement;
                            if (inputElement) {
                                if (inputElement.type === 'number' || inputElement.step === '0.01') {
                                    data[key] = parseFloat(value as string);
                                } else if (inputElement.type === 'date') {
                                    data[key] = value ? new Date(value as string).toISOString().split('T')[0] : null;
                                } else if (inputElement.type === 'checkbox') {
                                    data[key] = inputElement.checked;
                                }
                            }
                        }

                        const response = await fetch(url, {
                            method: RequestType,
                            headers: {
                                'Content-Type': 'application/json',
                                'RequestVerificationToken': (document.querySelector('input[name="__RequestVerificationToken"]') as HTMLInputElement)?.value
                            },
                            body: JSON.stringify(data)
                        });
                        await Form.HandleAjaxJsonResponse(response, validationElement, resultElement, onSuccess, onError, variables, resultFunc, resultType);

                    } catch (error) {
                        Constants.Global.Debug(Constants.LogLevel.error, 'Error submitting form:', error);
                        if (onError) onError(error);
                    }
                }
                /**
                 * Handles the AJAX JSON response from a form submission.
                 * @param response
                 * @param validationElement
                 * @param resultElement
                 * @param onSuccess
                 * @param onError
                 * @param variables
                 * @param resultFunc
                 * @param resultType
                 */
                static async HandleAjaxJsonResponse(
                    response: Response,
                    validationElement: PairJsonElement,
                    resultElement: PairJsonElement | null,
                    onSuccess: ((result: any) => void) | null,
                    onError: ((error: any) => void) | null,
                    variables: DropdownVariables,
                    resultFunc: ResultFuncType,
                    resultType: JsonResultTypes
                ): Promise<void> {
                    if (response.ok) {
                        if (resultElement) {
                            resultElement.Element.innerHTML = '';
                        }
                        validationElement.Element.innerHTML = '';
                        const result = await response.json();

                        if (result.success === false && result.errors) {
                            // Display validation errors
                            validationElement.Element.style.display = 'block';
                            for (const [key, messages] of Object.entries(result.errors)) {
                                (messages as string[]).forEach(message => {
                                    const errorItem = document.createElement('div');
                                    errorItem.className = validationElement.ItemClass;
                                    errorItem.textContent = `${key}: ${message}`;
                                    validationElement.Element.appendChild(errorItem);
                                });
                            }
                            validationElement.Element.scrollIntoView(variables.scrollParams);
                        } else if (onSuccess) {
                            onSuccess(result);
                        } else if (resultElement) { // Guard against null before populating default results
                            if (resultType === JsonResultTypes.KeyValuePair) {
                                for (const [key, value] of Object.entries(result)) {
                                    const resultItem = document.createElement('div');
                                    resultItem.className = resultElement.ItemClass;
                                    resultItem.appendChild(resultFunc(key, value));
                                    resultElement.Element.appendChild(resultItem);
                                }
                            } else if (resultType === JsonResultTypes.Scalar) {
                                resultElement.Element.appendChild(resultFunc('', Object.values(result).join(', ')));
                            }
                            resultElement.Element.scrollIntoView(variables.scrollParams);
                        }
                    } else {
                        Constants.Global.Debug(Constants.LogLevel.error, 'Error submitting form:', response.status, response.statusText);
                        if (onError) onError(response);
                    }
                }
                /**
                 * scrolls to the next input element in the form, or focuses on the first invalid input if the current one is the last
                 * @param currentInput
                 * @returns
                 */
                static FormNavigation(currentInput: HTMLInputElement): boolean {
                    const form: HTMLFormElement | null = currentInput.getNearestForm();
                    if (!form) return false;

                    const inputs = Array.from(form.querySelectorAll("input"));
                    const currentIndex = inputs.indexOf(currentInput);

                    if (currentIndex >= 0 && currentIndex < inputs.length - 1) {
                        const nextInput = inputs[currentIndex + 1] as HTMLInputElement;
                        nextInput.focus();
                        return true;
                    } else {
                        // If the current input is the last one, find the first invalid input within the same form
                        const firstInvalidInput = inputs.find(input => !input.checkValidity());
                        if (firstInvalidInput) {
                            firstInvalidInput.focus();
                        }
                        return false;
                    }
                }
                /** Set IDs from names for input elements without IDs */
                static SetInputIdsFromNames(): void {
                    const inputs: NodeListOf<HTMLInputElement> = document.querySelectorAll('input[name]:not([id])');
                    inputs.forEach((input) => {
                        const name = input.getAttribute('name');
                        const isValidId = name && /^[A-Za-z_][A-Za-z0-9_\-:.]*$/.test(name);
                        if (isValidId) {
                            input.id = name!;
                        } else {
                            Constants.Global.Debug(Constants.LogLevel.warn, `⚠️ Skipped input with invalid or missing name:`, input);
                        }
                    });
                    Constants.Global.Debug(Constants.LogLevel.log, "✅ Set IDs from names for input elements without IDs.");
                }
            }
            /** Dropdown Class - Handles dropdown functionality for input elements */
            export class Dropdown {

                /** Populates a dropdown with options based on the input value and excludes certain values  */
                static populateDropdown(
                    input: HTMLInputElement,
                    dropdown: HTMLDivElement,
                    options: DropdownOption[],
                    selectedValue: string | null = null,
                    excludeValues: string[] = [],
                    variables: DropdownVariables
                ): void {
                    dropdown.innerHTML = '';  // Clear previous list
                    const fragment = document.createDocumentFragment(); // Use DocumentFragment for better performance
                    options.forEach(option => {
                        if (excludeValues.includes(option.value) && option.value !== selectedValue) return;
                        const optionElement = document.createElement('div');
                        optionElement.classList.add(variables.itemClass);
                        optionElement.style.cursor = variables.itemCursor;
                        optionElement.innerHTML = option.text;
                        optionElement.dataset.value = option.value;
                        if (option.value.toLowerCase() === selectedValue?.toLowerCase()) {
                            optionElement.style.backgroundColor = dropdown.dataset.selectcolor || '';
                        }
                        optionElement.onclick = function () {
                            input.value = optionElement.dataset.value || '';
                            input?.dispatchEvent(new Event('change'));
                            Dropdown.highlightSelected(dropdown, optionElement, input.value);
                            dropdown.style.display = 'none';
                        };
                        fragment.appendChild(optionElement);
                    });
                    dropdown.appendChild(fragment); // Append all options at once
                    Dropdown.scrollToValue(dropdown, input, selectedValue, variables);
                }
                /** Scrolls to the selected value in the dropdown, highlighting it if found  */
                static scrollToValue(
                    dropdown: HTMLDivElement,
                    input: HTMLInputElement,
                    selectedValue: string | null,
                    variables: DropdownVariables
                ): void {
                    if (selectedValue) {
                        const selectedOption = Array.from(dropdown.children).find(
                            (option) => (option as HTMLElement).dataset.value?.toLowerCase() === selectedValue.toLowerCase()
                        ) as HTMLElement | undefined;

                        if (selectedOption) {
                            dropdown.querySelectorAll(`.${variables.selectClass}`).forEach((option) => {
                                (option as HTMLElement).classList.remove(variables.selectClass);
                            });
                            selectedOption.classList.add(variables.selectClass);
                            input.value = selectedOption.dataset.value || '';
                            selectedOption.scrollIntoView(variables.scrollParams);
                        }
                    }
                }
                /** Navigates through the dropdown items using arrow keys, wrapping around if necessary   */
                static navigateDropdown(
                    dropdown: HTMLDivElement,
                    isDown: boolean,
                    variables: DropdownVariables
                ): void {
                    const items = Array.from(dropdown.children).filter(
                        (item) => (item as HTMLElement).style.display !== 'none'
                    ) as HTMLElement[];

                    const selectedIndex = items.findIndex((item) =>
                        item.classList.contains(variables.selectClass)
                    );

                    if (selectedIndex !== -1) {
                        items[selectedIndex].classList.remove(variables.selectClass);
                    }

                    const nextIndex = isDown
                        ? (selectedIndex === items.length - 1 ? 0 : selectedIndex + 1)
                        : (selectedIndex === 0 ? items.length - 1 : selectedIndex - 1);

                    if (items[nextIndex]) {
                        items[nextIndex].classList.add(variables.selectClass);
                        items[nextIndex].scrollIntoView(variables.scrollParams);
                    }
                }
                /** Highlights the selected option in the dropdown, changing its background color if it matches the selected value   */
                static highlightSelected(
                    dropdown: HTMLDivElement,
                    selectedOption: HTMLElement,
                    selectedValue: string
                ): void {
                    Array.from(dropdown.children).forEach((option) => {
                        (option as HTMLElement).style.backgroundColor = '';
                    });
                    if (selectedOption.dataset.value?.toLowerCase() === selectedValue.toLowerCase()) {
                        selectedOption.style.backgroundColor = dropdown.dataset.selectcolor || '';
                    }
                }
                /** Filters the dropdown options based on the input value, showing only those that match */
                static filterDropdown(
                    input: HTMLInputElement,
                    dropdown: HTMLDivElement,
                    variables: DropdownVariables
                ): void {
                    const value = input.value.toLowerCase();
                    Array.from(dropdown.children).forEach(option => {
                        const text = (option as HTMLElement).innerText.toLowerCase();

                        // Use a case-insensitive comparison
                        (option as HTMLElement).style.display = text.includes(value) ? 'block' : 'none';
                        if ((option as HTMLElement).dataset.value?.toLowerCase() === value) {
                            input.value = (option as HTMLElement).dataset.value ?? "";
                        } else if (input.value.length > 0) {
                            dropdown.querySelectorAll(`.${variables.selectClass}`).forEach(option => (option as HTMLElement).classList.remove(variables.selectClass));
                            const firstMatchingOption = Array.from(dropdown.children).find(option => (option as HTMLElement).style.display === 'block');
                            if (firstMatchingOption) {
                                (firstMatchingOption as HTMLElement).classList.add(variables.selectClass);
                            }
                        }
                    });
                }
                /** Adds event listeners to input elements and their corresponding dropdowns for handling user interactions    */
                static addDropDownEvents(
                    inputs: HTMLInputElement[],
                    dropdowns: HTMLDivElement[],
                    Options: DropdownOption[],
                    variables: DropdownVariables
                ): void {
                    inputs.forEach((input, index) => {
                        const dropdown = dropdowns[index];
                        input.addEventListeners(['change', 'blur', 'click', 'focus', 'mousedown', 'input', 'keydown'], (event: Event) => {
                            const validationSpan = document.querySelector(`[data-valmsg-for="${input.id}"]`);
                            if (event.type === 'keydown' && (event as KeyboardEvent).key === "ArrowDown" || (event as KeyboardEvent).key === "ArrowUp") {
                                event.preventDefault();
                                if (dropdown.style.display === 'none') {
                                    dropdown.style.display = 'block';
                                    Dropdown.scrollToValue(dropdown, input, input.value, variables);
                                }
                                Dropdown.navigateDropdown(dropdown, (event as KeyboardEvent).key === "ArrowDown", variables);
                                return;
                            } else if ((event.type === 'keydown' && (event as KeyboardEvent).key === "Enter") || event.type === 'blur') {
                                let selectedOption = (event as KeyboardEvent).key === "Enter" ? dropdown.querySelector(`.${variables.itemClass}.${variables.selectClass}`) : null;
                                if (event.type === 'blur' || !selectedOption) {
                                    const matchedOption = Array.from(dropdown.children).find(option => (option as HTMLElement).dataset.value?.toLowerCase() === input.value.toLowerCase());
                                    if (matchedOption) { selectedOption = matchedOption; }
                                }
                                if (selectedOption) {
                                    input.value = (selectedOption as HTMLElement).dataset.value || '';
                                    Dropdown.highlightSelected(dropdown, selectedOption as HTMLElement, input.value);
                                } else {
                                    input.value = "";
                                    Dropdown.filterDropdown(input, dropdown, variables);
                                }
                                input.dispatchEvent(new Event('change'));
                                if (input.form && !input.form.checkValidity()) {
                                    event.preventDefault();
                                }
                                if (input.checkValidity()) {
                                    event.preventDefault();
                                }
                                if (event.type !== 'blur') {
                                    return;
                                }
                                if (event.type === 'blur') {
                                    Dropdown.inputValidation(input, validationSpan as HTMLElement, true);
                                    setTimeout(() => {
                                        dropdown.style.display = 'none';
                                        return;
                                    }, 100);
                                    return;
                                } else {
                                    dropdown.style.display = dropdown.style.display === 'block' ? 'none' : 'block';
                                    return;
                                }
                            }
                            if (event.type === 'keydown' && ((event as KeyboardEvent).code === "Escape" || (event as KeyboardEvent).key === "Tab")) {
                                dropdown.style.display = 'none';
                                return;
                            }
                            if (event.type === 'input') {
                                dropdown.style.display = 'block';
                                Dropdown.filterDropdown(input, dropdown, variables);
                                return;
                            }
                            if (event.type === 'mousedown') {
                                (input as any).dataset.lastMousedownTime = Date.now();
                                return;
                            }
                            if (event.type === 'focus') {
                                (input as any).dataset.lastFocusTime = Date.now();
                                if (Date.now() - parseInt((input.form as any)?.dataset.lastSubmitTime, 10) > 100) {
                                    Dropdown.inputValidation(input, validationSpan as HTMLElement, true);
                                }
                                const excludeValues = inputs.map(i => i.value).filter(value => value !== input.value);
                                Dropdown.populateDropdown(input, dropdown, Options, input.value, excludeValues, variables);
                                if ((input as any).dataset.lastMousedownTime && (Date.now() - parseInt((input as any).dataset.lastMousedownTime, 10)) < 100) {
                                    return;
                                }
                            }
                            if (event.type === 'focus' || event.type === 'click') {
                                dropdown.style.display = dropdown.style.display === 'block' ? (event.type === 'focus' && (Date.now() - parseInt((input.form as any)?.dataset.lastSubmitTime, 10) > 100) ? 'block' : 'none') : 'block';
                                if (dropdown.style.display === 'block') { Dropdown.positionDropdown(input, dropdown); }
                            }
                        });
                    });
                }
                /** Positions the dropdown below or above the input field based on its position in the viewport   */
                static positionDropdown(input: HTMLInputElement, dropdown: HTMLDivElement): void {
                    const rect = input.getBoundingClientRect();
                    const windowHeight = window.innerHeight;
                    const dropdownHeight = dropdown.offsetHeight;

                    if (rect.top >= windowHeight / 2) {
                        dropdown.style.top = `${input.offsetTop - dropdownHeight}px`;
                    } else {
                        dropdown.style.top = `${input.offsetTop + input.offsetHeight}px`;
                    }

                    dropdown.style.left = `${input.offsetLeft}px`;
                    dropdown.style.width = `${input.offsetWidth}px`;
                }

                static inputValidation(input: HTMLInputElement, validationSpan: HTMLElement, force: boolean = false): void {
                    if (input.checkValidity() || force) {
                        validationSpan.classList.remove('field-validation-error');
                        validationSpan.innerHTML = '';
                        validationSpan.classList.add('field-validation-valid');
                    }
                }
            }
        }
        export namespace Configuration {
            export class AppConfigLoader {
                static readonly elementId = 'AppConfigEl';
                static init(): void {
                    const configEl = document.getElementById(AppConfigLoader.elementId);
                    if (!configEl) return;

                    const config: Record<string, string | number | boolean> = {};

                    for (const [key, value] of Object.entries(configEl.dataset)) {
                        if (value === undefined) continue; // Guarantees `value` is string for remaining logic

                        if (value === "true") {
                            config[key] = true;
                        } else if (value === "false") {
                            config[key] = false;
                        } else if (value.trim() !== "" && !isNaN(Number(value))) {
                            config[key] = Number(value);
                        } else {
                            config[key] = value;
                        }
                    }

                    window.AppConfig = config;
                    Constants.Global.Debug(Constants.LogLevel.log, `[${AppConfigLoader.elementId}]`, window.AppConfig);
                }
            }
        }
        /** Constants Namespace - Contains global constants and utility functions for logging and URLs  */
        export namespace Constants {
            export enum ConfigKeys {
                DeveloperMode = "DeveloperMode",
                ScriptSeperator = "ScriptSeperator",
                LoaderImagesContentPath = "LoaderImagesContentPath"
            }
            export class Config {
                private static _vars: { [key: string]: string } | null = null;

                /** Loads the web variables from the server (only once) */
                static async Load(): Promise<void> {
                    if (this._vars) return; // already loaded
                    this._vars = await Html.Form.GetAjaxJson<{ [key: string]: string }>(Urls.DefaultGetPropUrl);
                }

                /** Gets a raw value by key */
                static get(key: string): string | undefined {
                    return this._vars?.[key];
                }
                /** Gets all keys and values as an array of tuples */
                static entries(): { [key: string]: string } {
                    return this._vars ? { ...this._vars } : {};
                }
            }
            /** WebVariables - Asynchronously loads web variables from the server */
            export const LogLevel = Object.fromEntries(
                Object.entries(console)
                    .filter(([key, value]) => typeof value === 'function')
                    .map(([key]) => [key, key])
            ) as {
                    [K in keyof Console as Console[K] extends (...args: any[]) => any ? K : never]: K
                };
            /** Type for log levels, e.g., 'log', 'warn', 'error', etc. */
            export type LogLevel = keyof typeof LogLevel;
            export class Global {
                /** Debug method to log messages based on the log level and developer mode */
                static async Debug(level: LogLevel, ...args: any[]): Promise<void> {
                    /*                    await Config.Load();*/
                    if (Config.get(ConfigKeys.DeveloperMode) && typeof console[level] === 'function') {
                        (console[level] as (...args: any[]) => void)(...args);
                    }
                }
            }
            /** Urls Class - Contains URLs for various API endpoints  */
            export class Urls {
                /** Default URL for getting properties from the server  */
                static DefaultGetPropUrl: string = `/WebVariables/GetProp`;
                /** 
                 * Constructs the default URL path used to retrieve application images.
                 * This path is dynamically built using the configured script separator.
                 * 
                 * Example Output:
                 * "/AppAssets/Content/General/Images/" (if scriptSeperator = "/")
                 */
                static get DefaultAppImagesPathUrl(): string {
                    return `${Config.get(ConfigKeys.LoaderImagesContentPath)}${Config.get(ConfigKeys.ScriptSeperator)}`;
                }
            }
        }
    }
} 